import { FluentProvider, teamsLightTheme, teamsDarkTheme, teamsHighContrastTheme, Theme } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { HashRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { app } from '@microsoft/teams-js';
import Privacy from './Privacy';
import TermsOfUse from './TermsOfUse';
import TabConfig from './TabConfig';
import { TeamsFxContext } from '../context';
import config from '../config';
import Launch from './Launch';
import { getLaunchUrlParts, isConfigTab } from '../services';

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function App() {
	let { tenantId } = config;

	// Get TenantId if not provided in query string
	if (!isConfigTab()) {
		// Check for specific hosts
		const launchURL = getLaunchUrlParts();

		// Fallback for prj svc tenant - TESTING
		if (launchURL.host.includes('tpgppmch')) {
			tenantId = 'd046153d-c10c-4a43-9c3f-052193846a9a';
		}
	}

	// Save tenantId in local storage for auth start and end redirects
	localStorage.setItem('pppTenantId', tenantId ?? '');

	const [loading, setLoading] = useState(true);
	const [themeString, setThemeString] = useState('default');
	const [theme, setTheme] = useState<Theme>({ ...teamsLightTheme, colorNeutralBackground3: '#F5F5F5' });

	useEffect(() => {
		(async () => {
			try {
				await app.initialize();
				app.notifySuccess();
				try {
					const ctx = await app.getContext();
					const t = ctx?.app?.theme ?? 'default';
					setThemeString(t);
					if (t === 'dark') setTheme(teamsDarkTheme);
					else if (t === 'contrast') setTheme(teamsHighContrastTheme);
					else setTheme({ ...teamsLightTheme, colorNeutralBackground3: '#F5F5F5' });
				} catch {
					// keep light theme defaults
				}
			} catch {
				// SDK initialization failed (e.g., not running inside Teams)
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	return (
		<TeamsFxContext.Provider value={{ theme, themeString }}>
			<FluentProvider
				theme={
					themeString === 'dark'
						? teamsDarkTheme
						: themeString === 'contrast'
						? teamsHighContrastTheme
						: {
								...teamsLightTheme,
								colorNeutralBackground3: '#F5F5F5',
						  }
				}
			>
				<Router>
					{!loading && (
						<Routes>
							<Route path='/privacy' element={<Privacy />} />
							<Route path='/termsofuse' element={<TermsOfUse />} />
							<Route path='/launch' element={<Launch />} />
							<Route path='/config' element={<TabConfig />} />
							<Route path='*' element={<Navigate to={'/launch'} />}></Route>
						</Routes>
					)}
				</Router>
			</FluentProvider>
		</TeamsFxContext.Provider>
	);
}
